import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Outlet } from 'react-router-dom';
import { unwrappedSelectedPlayerNameAtom } from '@/atoms/player/info';
import { globalPlayerIdAtom, playerIdAtom } from '@/atoms/player/player';
import { playerEventMetricFiltersAtom } from '@/atoms/queries/player/playerEventData';
import { ContentState } from '@/components/contentState/ContentState';
import { PlayerNav } from '@/components/navigation/subNav/PlayerNav';
import { SavePlayersToShortlistsWithNavigate } from '@/components/shortlists/SavePlayersToShortlistsWithNavigate';
import { EntityTopBar } from '@/components/topBar/EntityTopBar';
import { layoutScopeAtoms } from '@/consts/scope';
import { useSetPlayerComparisonDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetPlayerComparisonDebouncedFilterParams';
import { useSetPlayerEventDataDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetPlayerEventDataDebouncedFilterParams';
import { useSetPlayerLeagueAverageDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetPlayerLeagueAverageDebouncedFilterParams';
import { usePlayerReferenceData } from '@/hooks/dataFetching/usePlayerReferenceData';
import { useAutoResetPagination } from '@/hooks/useAutoResetPagination';
import { useAutoResetSelectionLayer } from '@/hooks/useAutoResetSelectionLayer';
import { useSetMetricKey } from '@/hooks/useMetricKey';
import { useResetFiltersOnAreaEntry } from '@/hooks/useResetFiltersOnAreaEntry';
import { useSetIdAtom } from '@/hooks/useSetIdAtom';
import { useSetUserPlayerSelectedAtom } from '@/hooks/useSetUserPlayerSelectedAtom';
import { StatusTranslationKey } from '@/types/status';
import { AppShellContents } from '../AppShell';
import { RecordRecentVisit } from '../RecordRecentVisit';
import { SectionWrapper } from '../SectionWrapper';

const PlayerLayoutInner = () => {
  usePlayerReferenceData();
  const playerEventMetricFilters = useAtomValue(playerEventMetricFiltersAtom);
  useAutoResetSelectionLayer(playerEventMetricFilters);
  useAutoResetPagination(playerEventMetricFilters);

  return (
    <SectionWrapper section="player">
      <Outlet />
    </SectionWrapper>
  );
};

const PlayerLayoutWithData = () => {
  useSetIdAtom(globalPlayerIdAtom, 'playerId');
  useSetIdAtom(playerIdAtom, 'playerId');
  useSetUserPlayerSelectedAtom();
  useSetMetricKey();

  const playerId = useAtomValue(playerIdAtom);

  return (
    <AppShellContents
      topBar={
        <EntityTopBar
          selectedEntityNameAtom={unwrappedSelectedPlayerNameAtom}
          actions={playerId && <SavePlayersToShortlistsWithNavigate playerIds={[playerId.toString()]} />}
        />
      }
      subNav={<PlayerNav />}
    >
      <RecordRecentVisit type="player" />
      {playerId ? <PlayerLayoutInner /> : <ContentState status={StatusTranslationKey.LOADING} />}
    </AppShellContents>
  );
};

export const SetPlayerDataDebouncedFilterParams = () => {
  useSetPlayerEventDataDebouncedFilterParams();
  useSetPlayerComparisonDebouncedFilterParams();
  useSetPlayerLeagueAverageDebouncedFilterParams();
  return null;
};

export const PlayerLayout = () => {
  useResetFiltersOnAreaEntry();
  return (
    <ScopeProvider atoms={layoutScopeAtoms}>
      <SetPlayerDataDebouncedFilterParams />
      <PlayerLayoutWithData />
    </ScopeProvider>
  );
};
